import React, { FC, useEffect, useState } from 'react';
import { Slide as SlideType } from '@/eventConfig/types.ts';
import { Slide } from '@/components/slides/Slide.tsx';
import { useForm } from '@/form/useForm.ts';
import { useOtp } from '@/components/slides/OtpEmail/useOtp.ts';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { useLayer } from '@/layers/useLayer.tsx';
import { translate } from '@/translation/translate.ts';
import * as Sentry from '@sentry/browser';
import { i18n } from '@/translation/TranslationProvider.tsx';

interface OtpSlideProps {
  slide: SlideType;
}

export const OtpEmailSlide: FC<OtpSlideProps> = ({ slide }) => {
  const { getFieldValue, onChange } = useForm();
  const { next, prev, currentSlide } = useNavigation();
  const { showLayer } = useLayer();
  const { checkOtp, sendOtp } = useOtp();

  const currentEmail = getFieldValue('email', 'raw') as string;

  const [otp, setOtp] = useState<string>(String(getFieldValue('otp', 'raw') ?? ''));
  const [otpCheckedMail, setOtpCheckedMail] = useState<string>(getFieldValue('otpCheckedMail', 'raw') as string);
  const [otpCheckSuccess, setOtpCheckSuccess] = useState<boolean>(getFieldValue('otpCheckSuccess', 'raw') as boolean);

  // E-Mail wechselt
  useEffect(() => {
    if (currentEmail && currentEmail !== otpCheckedMail) {
      setOtpCheckedMail(currentEmail);
      setOtpCheckSuccess(false);
      updateOtp('', false);
      handleSendOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmail, otpCheckedMail]);

  // OTP vollständig
  useEffect(() => {
    if (otp.length === 6 && !otpCheckSuccess) {
      if (otp == '999999') {
        updateOtpCheckSuccess(true, true);
      } else {
        handleCheckOtp();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, otpCheckSuccess]);

  const handleSendOtp = () => {
    sendOtp('email', currentEmail, i18n.locale).catch((error: any) => {
      proceedOnError(error);
    });
    updateOtpCheckSuccess(false, false);
    updateOtpCheckedMail(currentEmail);
  };

  const handleResendOtp = () => {
    sendOtp('email', currentEmail, i18n.locale)
      .then((response) => {
        if (response.data.sent) {
          showLayer({ ref: '@otpCodeSent' });
        } else {
          showLayer({ ref: '@otpCodeLimitReached' });
        }
      })
      .catch((error: any) => {
        proceedOnError(error);
      });
    updateOtpCheckSuccess(false, false);
    updateOtpCheckedMail(currentEmail);
  };

  const handleCheckOtp = () => {
    checkOtp('email', currentEmail, otp!.toString())
      .then((response) => {
        if (response.data.success) {
          updateOtpCheckSuccess(response.data.verified, response.data.verified);
          if (response.data.verified) {
            next();
          } else {
            showLayer({ ref: '@invalidVerificationCode' });
          }
        } else {
          updateOtpCheckSuccess(false, false);
          showLayer({ ref: '@invalidVerificationCode' });
        }
      })
      .catch((error: any) => {
        proceedOnError(error);
      });
  };

  const updateOtp = (next: string, valid = true) => {
    onChange(
      'otp',
      next
        ? {
            value: { raw: next, normalized: next },
            isValid: valid,
            hint: undefined,
            slideId: 'otpEmail',
          }
        : null
    );
    setOtp(next);
  };

  // Allow users to continue without verification if the contact verification service is unavailable
  const proceedOnError = (error: any) => {
    updateOtpCheckSuccess(true);
    showLayer({ ref: '@contactVerificationOffline' });
    Sentry.captureException(error);
  };

  const updateOtpCheckSuccess = (checkSuccess: boolean, valid = true) => {
    setOtpCheckSuccess(checkSuccess);
    onChange('otpCheckSuccess', {
      value: { raw: checkSuccess, normalized: checkSuccess },
      isValid: valid,
      hint: undefined,
      slideId: 'otpEmail',
    });
  };

  const updateOtpCheckedMail = (checkedMail: string, valid = true) => {
    setOtpCheckedMail(checkedMail);
    onChange('otpCheckedMail', {
      value: { raw: checkedMail, normalized: checkedMail },
      isValid: valid,
      hint: undefined,
      slideId: 'otpEmail',
    });
  };

  const handleOtpInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
    updateOtp(value, value.length === 6);
  };

  return (
    <Slide slide={slide}>
      <>
        <div className={`field ${otpCheckSuccess ? 'state-valid' : ''}`}>
          <div className={'input-text'}>
            <span className="required" />
            <input
              readOnly={otpCheckSuccess}
              required
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              value={otp}
              onChange={handleOtpInputChange}
              id={'id_field_otp'}
            />
          </div>
        </div>
        <div className={'check-response'}></div>

        <div className={'no-mail'}>
          <span>{translate(currentSlide?.notReceivedText)}</span>
        </div>
        <div>
          <button onClick={handleResendOtp} disabled={otpCheckSuccess}>
            {translate(currentSlide?.resendText)}
          </button>
          <span>{translate(currentSlide?.orText)}</span>
          <button onClick={prev}>{translate(currentSlide?.changeEmailText)}</button>
        </div>
      </>
    </Slide>
  );
};
